import { useRef, useState } from "react";
import { DeviceTree } from "../components/DeviceTree/DeviceTree";
import { LayoutPicker } from "../components/Grid/LayoutPicker";
import { VideoGrid } from "../components/Grid/VideoGrid";
import { useGridStore } from "../store/gridStore";
import { Button } from "../components/common/Button";
import { PtzControlPanel } from "../components/Ptz/PtzControlPanel";
import { GuardiaVirtualModal } from "../components/GuardiaVirtual/GuardiaVirtualModal";
import { GuardiaBadge } from "../components/GuardiaVirtual/GuardiaBadge";
import { useGuardiaVirtual } from "../hooks/useGuardiaVirtual";
import { useGuardiaStore } from "../store/guardiaStore";
import { Trash2, Maximize, Minimize, Move, Radar } from "lucide-react";

export default function LiveViewPage() {
  const preset = useGridStore((s) => s.preset);
  const setPreset = useGridStore((s) => s.setPreset);
  const clearAll = useGridStore((s) => s.clearAll);
  const isMaximized = useGridStore((s) => s.isMaximized());
  const fillCells = useGridStore((s) => s.fillCells);
  const toggleFillCells = useGridStore((s) => s.toggleFillCells);
  const cells = useGridStore((s) => s.cells);
  const setAllSubtype = useGridStore((s) => s.setAllSubtype);

  const [ptzOpen, setPtzOpen] = useState(false);
  const [guardModalOpen, setGuardModalOpen] = useState(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const guardiaRunning = useGuardiaStore((s) => s.running);
  const guardia = useGuardiaVirtual(gridContainerRef);

  // Celdas con canal asignado (las vacias no cuentan para el estado del toggle).
  const activeCells = cells.filter((c) => c.deviceId != null && c.channel != null);
  const hasActive = activeCells.length > 0;
  const allMain = hasActive && activeCells.every((c) => c.subtype === 0);
  const allSub = hasActive && activeCells.every((c) => c.subtype === 1);

  return (
    <div className="flex h-full">
      <aside className="w-60 flex-shrink-0">
        <DeviceTree />
      </aside>
      <section className="flex-1 flex flex-col">
        <div className="flex items-center justify-between bg-panel border-b border-line px-2 py-1">
          <LayoutPicker
            value={preset}
            onChange={setPreset}
            disabled={isMaximized || guardiaRunning}
          />
          <div className="flex items-center gap-2">
            <Button
              variant={allMain ? "primary" : "ghost"}
              size="sm"
              onClick={() => setAllSubtype(0)}
              disabled={!hasActive || guardiaRunning}
              title="Cambiar todos los canales a calidad principal (mayor resolucion, mas ancho de banda)"
            >
              Main Stream
            </Button>
            <Button
              variant={allSub ? "primary" : "ghost"}
              size="sm"
              onClick={() => setAllSubtype(1)}
              disabled={!hasActive || guardiaRunning}
              title="Cambiar todos los canales a calidad secundaria (menor resolucion, menos ancho de banda)"
            >
              Sub Stream
            </Button>
            <Button
              variant={fillCells ? "primary" : "ghost"}
              size="sm"
              onClick={toggleFillCells}
              title={fillCells
                ? "Volver al ajuste con aspect ratio (bandas negras si no calza)"
                : "Estirar cada video al tamano completo de su casilla"}
            >
              {fillCells ? <Minimize size={12} /> : <Maximize size={12} />} Ventana completa
            </Button>
            <Button
              variant={ptzOpen ? "primary" : "ghost"}
              size="sm"
              onClick={() => setPtzOpen((v) => !v)}
              disabled={guardiaRunning}
              title="Controles PTZ para la camara seleccionada"
            >
              <Move size={12} /> PTZ
            </Button>
            <Button
              variant={guardiaRunning || guardModalOpen ? "primary" : "ghost"}
              size="sm"
              onClick={() => {
                if (guardiaRunning) {
                  guardia.stop();
                  return;
                }
                setPtzOpen(false);
                setGuardModalOpen(true);
              }}
              title="Recorrido automatico de todas las camaras (Guardia Virtual)"
            >
              <Radar size={12} /> Guardia Virtual
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={guardiaRunning}
              title="Limpiar grilla"
            >
              <Trash2 size={12} /> Limpiar
            </Button>
          </div>
        </div>
        <div ref={gridContainerRef} className="relative flex-1 overflow-hidden">
          <VideoGrid />
          <GuardiaBadge />
        </div>
      </section>
      {ptzOpen && <PtzControlPanel onClose={() => setPtzOpen(false)} />}
      <GuardiaVirtualModal
        open={guardModalOpen}
        onCancel={() => setGuardModalOpen(false)}
        onStart={(sec, grid) => {
          setGuardModalOpen(false);
          guardia.start(sec, grid);
        }}
      />
    </div>
  );
}
